import React, { useState } from 'react';
import { hot } from 'react-hot-loader';
import Button from '../Elements/Button/Button';
import Checkbox from '../Elements/Checkbox/Checkbox';
import { purifier, getCookie, setCookie } from '../../utils/dom';
import { translate } from '../../utils/locale';
import s from './CookiesNotification.scss';

const CookiesNotification = ({ content, setup, cookieConsent }) => {
	const notifications = content.filter((n) => n?.text || n?.manageable); // only notifications which are set.
	const [elementRemoved, removingElement] = useState(false);
	const [showCookie, toggleCookie] = useState(true);
	const [showManageSettings, toggleManageSettings] = useState(false);
	const [cookieSettings, updateCookieSettings] = useState(['all']);
	let isClosing = false;

	if (!showCookie) return null;

	if (!showCookie) {
		// Remove element
		isClosing = true;
		window.setTimeout(() => {
			removingElement(true);
			isClosing = false;
		}, 2000);

		if (elementRemoved) return null;
	}

	const manageSettingsHandler = (e) => {
		e.preventDefault();
		toggleManageSettings(true);
		if (cookieSettings.indexOf('all') !== -1) updateCookieSettings(['default']);
	};

	const updateCookieSettingsHandler = (id, value) => {
		if (id === 'default') return;
		if (cookieSettings.indexOf('all') !== -1) updateCookieSettings(['default']);
		if (value && cookieSettings.indexOf(id) === -1) updateCookieSettings((prevState) => [...prevState, id]);
		if (!value && cookieSettings.indexOf(id) !== -1) updateCookieSettings((prevState) => [...prevState.slice(0, prevState.indexOf(id)), ...prevState.slice(prevState.indexOf(id) + 1)]);
	};

	return (
		<div className={s.notification}>
			{
				notifications.map((item) => {
					if (item.countryRestrict && item.countryRestrict.indexOf(setup.country) === -1 && !item?.text) return null;
					const notificationInnerClasses = [s.notification_inner, (item.removeText ? [s.notification_inner__closeText] : null)];
					const manageFields = (item.manageFields && item.manageFields.filter((field) => field.key)) || [];
					const style = {};

					if (item.backgroundColor) style.backgroundColor = item.backgroundColor;
					if (showManageSettings) notificationInnerClasses.push(s.notification_inner__manage);

					// Check for cookies which are already set.
					if ((item.removable) && item.cookieDuration) {
						let cookieIsSet = false;

						const cookies = typeof document !== 'undefined' ? getCookie('cookies', true) : JSON.parse((setup?.cookies?.cookies || false));
						if (cookies && cookies !== 1) cookieIsSet = true;

						// Still render notification if from server and cookie not set on page load.
						// This enables us to fade it out.
						if ((cookieIsSet && !isClosing) || (!isClosing && !showCookie)) return null;
					}

					const removeNotificationHandler = (e, override = false) => {
						e.preventDefault();

						if (cookieSettings.indexOf('all') !== -1 || cookieSettings.length === manageFields.length || override) {
							setCookie('cookies', ['all'], item.cookieDuration);
							cookieConsent(['all']);
							fetch('/cookie-consent-status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ country: setup.country, permissions: ['all'] }) });
						} else {
							setCookie('cookies', cookieSettings, item.managedCookieDuration);
							cookieConsent(cookieSettings);
							fetch('/cookie-consent-status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ country: setup.country, permissions: cookieSettings }) });
						}

						const consent = {};

						if (typeof window.gtag !== 'undefined') {
							consent.functionality_storage = 'granted';
							consent.security_storage = 'granted';
							consent.ad_storage = cookieSettings.indexOf('marketing') !== -1 || cookieSettings.indexOf('all') !== -1 ? 'granted' : 'denied';
							consent.ad_user_data = cookieSettings.indexOf('marketing') !== -1 || cookieSettings.indexOf('all') !== -1 ? 'granted' : 'denied';
							consent.ad_personalization = cookieSettings.indexOf('marketing') !== -1 || cookieSettings.indexOf('all') !== -1 ? 'granted' : 'denied';
							consent.analytics_storage = cookieSettings.indexOf('analytics') !== -1 || cookieSettings.indexOf('all') !== -1 ? 'granted' : 'denied';
							consent.personalization_storage = 'granted';

							window.gtag('consent', 'update', consent);
							if (typeof window.dataLayer === 'undefined') window.dataLayer = [];
							window.dataLayer.push({
								event: 'ConsentUpdated',
								values: {},
							});
						}

						toggleCookie(false);
					};

					const acceptAllHandler = (e) => {
						e.preventDefault();
						removeNotificationHandler(e, true);
					};

					return (
						<div className={[...notificationInnerClasses, (!showCookie ? s.notification_inner__exiting : null)].join(' ')}>
							<div className={s.notification_inner_container} style={style}>
								{
									showManageSettings ? (
										<>
											<div className={s.notification_inner_container__manage}>
												<h4>{ translate(setup, item.manageTitle) }</h4>
												{
													manageFields.map((field) => (
														<Checkbox variantClasses={['checkbox__white']} key={field.key} id={field.cookieKey} selected={field.cookieKey === 'default' || cookieSettings.indexOf(field.cookieKey) !== -1} inputChange={updateCookieSettingsHandler}>
															<h5>{ translate(setup, field.title) }</h5>
															<div dangerouslySetInnerHTML={{ __html: purifier(translate(setup, field.content), { ALLOWED_TAGS: ['a'] }) }} />
														</Checkbox>
													))
												}
												<div className={s.notification_inner_container_submit} style={style}>
													<Button type="button" variantClasses={['button__fullWidth', 'button__outlineHover']} clickEventHandler={acceptAllHandler}>{ translate(setup, item.acceptAllText) }</Button>
													<Button type="button" variantClasses={['button__fullWidth', 'button__white']} clickEventHandler={removeNotificationHandler}>{ translate(setup, item.saveManageFields) }</Button>
												</div>
											</div>
										</>
									) : (
										<>
											<div className={s.notification_inner_container_text} dangerouslySetInnerHTML={{ __html: purifier(translate(setup, item?.text), { ALLOWED_TAGS: ['a'] }) }} />
											<div className={s.notification_inner_container_button}>
												<Button type="button" variantClasses={['button__fullWidth', 'button__white']} clickEventHandler={removeNotificationHandler}>{ translate(setup, item.removeText) }</Button>
											</div>
											<div className={s.notification_inner_container_button}>
												<Button type="button" variantClasses={['button__fullWidth', 'button__outlineHover']} clickEventHandler={manageSettingsHandler}>{ translate(setup, item.manageText) }</Button>
											</div>
										</>
									)
								}
							</div>
						</div>
					);
				})
			}
		</div>
	);
};

export default hot(module)(CookiesNotification);
